import { useState } from 'react';
import { X, Calendar, FileText, CheckCircle, XCircle, MapPin, Building } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { approveReservation, denyReservation } from '../lib/api';
import { useToast } from './ui/use-toast';
import { MachineReservation } from '../types/map';

interface ReservationReviewModalProps {
  reservation: MachineReservation;
  onClose: () => void;
  onSuccess: () => void;
}

export function ReservationReviewModal({ reservation, onClose, onSuccess }: ReservationReviewModalProps) {
  const [mode, setMode] = useState<'view' | 'approve' | 'deny'>('view');
  const [reservationStart, setReservationStart] = useState(reservation.reservationStart || '');
  const [reservationEnd, setReservationEnd] = useState(reservation.reservationEnd || '');
  const [notes, setNotes] = useState(reservation.notes || '');
  const [denialReason, setDenialReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const { toast } = useToast();

  const handleApprove = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!reservationStart || !reservationEnd) {
      toast({
        title: 'Error',
        description: 'Please set reservation start and end dates',
        variant: 'destructive',
      });
      return;
    }

    const start = new Date(reservationStart);
    const end = new Date(reservationEnd);
    if (end <= start) {
      toast({
        title: 'Error',
        description: 'End date must be after start date',
        variant: 'destructive',
      });
      return;
    }

    try {
      setSubmitting(true);
      await approveReservation(reservation.id, {
        reservationStart,
        reservationEnd,
        notes: notes || undefined,
      });

      toast({
        title: 'Success',
        description: 'Reservation approved successfully',
      });

      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error approving reservation:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to approve reservation',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeny = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!denialReason.trim()) {
      toast({
        title: 'Error',
        description: 'Please provide a reason for denial',
        variant: 'destructive',
      });
      return;
    }

    try {
      setSubmitting(true);
      await denyReservation(reservation.id, { denialReason });

      toast({
        title: 'Success',
        description: 'Reservation denied',
      });

      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error denying reservation:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to deny reservation',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Not specified';
    return new Date(dateString).toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  return (
    <div className="fixed inset-0 md:left-60 md:inset-y-0 md:right-0 bg-black/50 flex items-center justify-center z-[100] p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold">Review Reservation Request</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Request Information */}
          <div className="bg-gray-50 rounded-lg p-4 space-y-3">
            <div className="flex items-start gap-3">
              <Building className="h-5 w-5 text-gray-600 mt-0.5" />
              <div>
                <div className="text-sm font-medium text-gray-700">Brand</div>
                <div className="text-base font-semibold">{reservation.Brand?.name || 'Unknown'}</div>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <MapPin className="h-5 w-5 text-gray-600 mt-0.5" />
              <div>
                <div className="text-sm font-medium text-gray-700">Machine</div>
                <div className="text-base font-semibold">{reservation.Machine?.name || 'Unknown'}</div>
                <div className="text-sm text-gray-600">
                  {reservation.Machine?.venueType}
                  {reservation.Machine?.venueName ? ` • ${reservation.Machine.venueName}` : ''}
                </div>
                {reservation.Machine?.location && (
                  <div className="text-sm text-gray-500">{reservation.Machine.location}</div>
                )}
              </div>
            </div>

            <div className="flex items-start gap-3">
              <FileText className="h-5 w-5 text-gray-600 mt-0.5" />
              <div>
                <div className="text-sm font-medium text-gray-700">Requested By</div>
                <div className="text-base font-semibold">
                  {reservation.requester?.firstName || reservation.requester?.lastName
                    ? `${reservation.requester.firstName || ''} ${reservation.requester.lastName || ''}`.trim()
                    : reservation.requester?.email || 'Unknown User'}
                </div>
                {reservation.requester?.email && (reservation.requester?.firstName || reservation.requester?.lastName) && (
                  <div className="text-sm text-gray-500">{reservation.requester.email}</div>
                )}
                <div className="text-xs text-gray-400 mt-1">
                  Requested on {formatDate(reservation.requestedAt)}
                </div>
              </div>
            </div>

            {(() => {
              // Check if notes is JSON metadata (tender request data) or actual user notes
              if (!reservation.notes) return null;
              
              try {
                const data = JSON.parse(reservation.notes);
                // If it's JSON with tender metadata structure, don't show it
                if (data.venueSelections || data.retailMediaSelections || data.reportingTier) {
                  return null;
                }
                // If it's JSON but not our structure, show as-is
                return (
                  <div className="pt-3 border-t">
                    <div className="text-sm font-medium text-gray-700 mb-1">Request Notes</div>
                    <div className="text-sm text-gray-600 bg-white rounded p-3">{reservation.notes}</div>
                  </div>
                );
              } catch {
                // Not JSON, it's actual user notes - show it
                return (
                  <div className="pt-3 border-t">
                    <div className="text-sm font-medium text-gray-700 mb-1">Request Notes</div>
                    <div className="text-sm text-gray-600 bg-white rounded p-3">{reservation.notes}</div>
                  </div>
                );
              }
            })()}

            {reservation.reservationStart && reservation.reservationEnd && (
              <div className="pt-3 border-t">
                <div className="text-sm font-medium text-gray-700 mb-2">Requested Dates</div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-white rounded p-2">
                    <div className="text-xs text-gray-500">Start</div>
                    <div className="text-sm font-medium">{formatDate(reservation.reservationStart)}</div>
                  </div>
                  <div className="bg-white rounded p-2">
                    <div className="text-xs text-gray-500">End</div>
                    <div className="text-sm font-medium">{formatDate(reservation.reservationEnd)}</div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Action Forms */}
          {mode === 'view' && (
            <div className="flex gap-3">
              <Button
                onClick={() => setMode('approve')}
                className="flex-1 bg-green-600 hover:bg-green-700"
              >
                <CheckCircle className="h-4 w-4 mr-2" />
                Approve Reservation
              </Button>
              <Button
                onClick={() => setMode('deny')}
                variant="destructive"
                className="flex-1"
              >
                <XCircle className="h-4 w-4 mr-2" />
                Deny Request
              </Button>
            </div>
          )}

          {mode === 'approve' && (
            <form onSubmit={handleApprove} className="space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-md p-4">
                <h3 className="font-medium text-green-900 mb-3">Set Reservation Dates</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="reservationStart" className="flex items-center gap-2 text-green-900">
                      <Calendar className="h-4 w-4" />
                      Start Date *
                    </Label>
                    <Input
                      id="reservationStart"
                      type="date"
                      value={reservationStart}
                      onChange={(e) => setReservationStart(e.target.value)}
                      required
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label htmlFor="reservationEnd" className="flex items-center gap-2 text-green-900">
                      <Calendar className="h-4 w-4" />
                      End Date *
                    </Label>
                    <Input
                      id="reservationEnd"
                      type="date"
                      value={reservationEnd}
                      onChange={(e) => setReservationEnd(e.target.value)}
                      min={reservationStart || undefined}
                      required
                      className="mt-1"
                    />
                  </div>
                </div>

                <div className="mt-4">
                  <Label htmlFor="approvalNotes" className="text-green-900">
                    Additional Notes (Optional)
                  </Label>
                  <Textarea
                    id="approvalNotes"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Add any notes about the approved reservation..."
                    rows={3}
                    className="mt-1"
                  />
                </div>
              </div>

              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setMode('view')}
                  disabled={submitting}
                  className="flex-1"
                >
                  Back
                </Button>
                <Button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 bg-green-600 hover:bg-green-700"
                >
                  {submitting ? 'Approving...' : 'Confirm Approval'}
                </Button>
              </div>
            </form>
          )}

          {mode === 'deny' && (
            <form onSubmit={handleDeny} className="space-y-4">
              <div className="bg-red-50 border border-red-200 rounded-md p-4">
                <h3 className="font-medium text-red-900 mb-3">Reason for Denial</h3>
                <Textarea
                  value={denialReason}
                  onChange={(e) => setDenialReason(e.target.value)}
                  placeholder="Please provide a clear reason for denying this reservation request..."
                  rows={4}
                  required
                  className="border-red-300 focus:border-red-500 focus:ring-red-500"
                />
                <p className="text-xs text-red-700 mt-2">
                  This reason will be visible to the brand that made the request.
                </p>
              </div>

              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setMode('view')}
                  disabled={submitting}
                  className="flex-1"
                >
                  Back
                </Button>
                <Button
                  type="submit"
                  disabled={submitting}
                  variant="destructive"
                  className="flex-1"
                >
                  {submitting ? 'Denying...' : 'Confirm Denial'}
                </Button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

